import { forwardRef, useEffect, useRef, useState } from 'react'
import {
  Tab as Tabs,
  type TabGroupProps,
  type TabListProps,
  type TabProps,
  type TabPanelsProps,
  type TabPanelProps,
} from '@headlessui/react'
import { classNames } from '~/helpers/string'
import ArrowLeft from '~/components/icons/ArrowLeft'
import ArrowRight from '~/components/icons/ArrowRight'

function cn(defaultClass: string, className?: string | ((bag: any) => string)) {
  return typeof className === 'function'
    ? (bag: any) => classNames(defaultClass, className(bag))
    : classNames(defaultClass, className ?? '')
}

export const TabGroup = forwardRef<HTMLDivElement, TabGroupProps<'div'>>((props, ref) => {
  const { className, ...rest } = props
  return <Tabs.Group as="div" ref={ref} className={cn('relative', className)} {...rest} />
})

export const TabList = forwardRef<HTMLDivElement, TabListProps<'div'>>((props, ref) => {
  const { className, ...rest } = props
  const tabListRef = useRef<HTMLDivElement>(null)
  const [showArrows, setShowArrows] = useState({ left: false, right: false })

  const updateArrowsVisibility = () => {
    if (tabListRef.current) {
      const { scrollWidth, clientWidth, scrollLeft } = tabListRef.current
      setShowArrows({
        left: scrollLeft > 0,
        right: scrollLeft + clientWidth < scrollWidth - 5,
      })
    }
  }

  useEffect(() => {
    updateArrowsVisibility()
    window.addEventListener('resize', updateArrowsVisibility)
    tabListRef.current?.addEventListener('scroll', updateArrowsVisibility)

    return () => {
      window.removeEventListener('resize', updateArrowsVisibility)
      tabListRef.current?.removeEventListener('scroll', updateArrowsVisibility)
    }
  }, [])

  const scrollLeft = () => {
    if (tabListRef.current) {
      tabListRef.current.scrollBy({ left: -200, behavior: 'smooth' })
    }
  }

  const scrollRight = () => {
    if (tabListRef.current) {
      tabListRef.current.scrollBy({ left: 200, behavior: 'smooth' })
    }
  }

  return (
    <>
      <button
        aria-hidden={!showArrows.left}
        tabIndex={showArrows.left ? 0 : -1}
        onClick={scrollLeft}
        className="absolute left-0 top-2 z-10 flex h-8 w-8 transform items-center justify-center rounded-full bg-white drop-shadow-[0_4px_14px_rgba(0,0,0,0.1)] filter transition aria-hidden:pointer-events-none aria-hidden:opacity-0"
        aria-label="Scroll left"
      >
        <ArrowLeft className="mr-px" />
      </button>
      <Tabs.List
        as="div"
        ref={(node: HTMLDivElement) => {
          // @ts-ignore
          tabListRef.current = node
          if (typeof ref === 'function') ref(node)
          else if (ref) ref.current = node
        }}
        className={cn(
          'scrollbar-none flex w-full flex-row flex-nowrap gap-0 overflow-y-hidden overflow-x-scroll',
          className,
        )}
        {...rest}
      />
      <button
        aria-hidden={!showArrows.right}
        tabIndex={showArrows.right ? 0 : -1}
        onClick={scrollRight}
        className="absolute right-0 top-2 z-10 flex h-8 w-8 transform items-center justify-center rounded-full bg-white drop-shadow-[0_4px_14px_rgba(0,0,0,0.1)] filter transition aria-hidden:pointer-events-none aria-hidden:opacity-0"
        aria-label="Scroll right"
      >
        <ArrowRight className="ml-px" />
      </button>
      <div aria-hidden className="h-px w-full rounded-full bg-brand-neutral-200" />
    </>
  )
})

export const Tab = forwardRef<HTMLButtonElement, TabProps<'button'>>((props, ref) => {
  const { className, ...rest } = props
  return (
    <Tabs
      as="button"
      ref={ref}
      className={cn(
        // "outline" is disabled because of incorrectly working focus
        // See https://github.com/tailwindlabs/headlessui/issues/1694
        "relative whitespace-pre rounded-sm px-4 py-3.5 text-sm font-normal outline-none before:absolute before:bottom-0 before:left-0 before:right-0 before:h-[2px] before:rounded-full before:content-[''] aria-selected:text-brand-purple-700 before:aria-selected:bg-brand-purple-700",
        className,
      )}
      {...rest}
    />
  )
})

export const TabPanels = forwardRef<HTMLDivElement, TabPanelsProps<'div'>>((props, ref) => {
  const { className, ...rest } = props
  return <Tabs.Panels as="div" ref={ref} className={cn('mt-8', className)} {...rest} />
})

export const TabPanel = forwardRef<HTMLDivElement, TabPanelProps<'div'>>((props, ref) => {
  const { className, ...rest } = props
  return <Tabs.Panel as="div" ref={ref} className={cn('flex flex-col gap-6', className)} {...rest} />
})
